import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { LoggerService } from '../service/logger/logger.service';
import { SharepointService } from '@/shared/sharepoint/services/sharepoint.service';
import { InterestAccessItems } from '@/shared/sharepoint/interfaces/sharepoint';
import { SharepointAccessList } from '@/core/interfaces/sharepoint-access.interface';
import { NotificationHelper, UserType } from '@/core/helpers/notification.helper';
import { UserDataStore } from '@/core/store/user-store/user-data.store';
import { NotificationsReservoir } from '@/shared/dashboard/components/reservorio-notificaciones';

export interface NotificationItem {
  listName: string;
  category: string;
  subcategory: string;
  title: string;
  sectionPath: string;
}

type NotificationsStoreState = {
  items: InterestAccessItems[];
  loading: boolean;
  error: boolean;
  accessibleRepositories: SharepointAccessList[];
};

const initialState: NotificationsStoreState = {
  items: [],
  loading: false,
  error: false,
  accessibleRepositories: [],
};

export const NotificationsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed(store => {
    const notificationHelper = inject(NotificationHelper);

    return {
      /**
       * Indica si hay nuevo contenido disponible
       */
      hasNewContent: computed(() => store.items().length > 0),

      /**
       * Lista plana de notificaciones con los campos relevantes
       */
      notifications: computed((): NotificationItem[] =>
        store.items().map(item => ({
          listName: item.listName,
          category: item.category,
          subcategory: item.subCategory,
          title: item.title,
          sectionPath: notificationHelper.getNotificationPath(item.listName),
        })),
      ),

      /**
       * Indica si está cargando
       */
      isLoading: computed(() => store.loading()),

      /**
       * Indica si hubo un error
       */
      hasError: computed(() => store.error()),
    };
  }),

  withMethods(
    (
      store,
      logger = inject(LoggerService),
      sharepointService = inject(SharepointService),
      notificationHelper = inject(NotificationHelper),
      userStore = inject(UserDataStore),
    ) => ({
      /**
       * Carga los items de nuevo contenido desde SharePoint
       */
      async loadNotifications(): Promise<void> {
        patchState(store, { loading: true, error: false });

        try {
          logger.log('NotificationsStore - loadNotifications - start', 'NotificationsStore');

          // Obtener repositorios accesibles según shipto seleccionado
          const canales = [...new Set(userStore.getChannelsDistribution() ?? [])];
          const userType: UserType = {
            isRetailFuels: userStore.isRetailFuels(),
            isRetailLubes: userStore.isRetailLubes(),
            isRetailSpot: userStore.isRetailSpot(),
            isRetailFuelsWithRCOR: userStore.isRetailFuels() && userStore.isRCOR(),
          };
          const accessibleRepositories = notificationHelper.getAccessibleSharepointRepositories(
            canales,
            userType,
            'notificaciones',
          );

          // Cargar todos los items de nuevos contenidos
          const allItems = await sharepointService.getInterestAccess(NotificationsReservoir.NOTIFICATIONS);

          // Filtrar items según repositorios accesibles por el shipto seleccionado (spot, fuels, etc.)
          const filteredItems = allItems.filter(item =>
            accessibleRepositories.some(repo => repo.repository === item.listName),
          );

          patchState(store, {
            items: filteredItems,
            accessibleRepositories,
            loading: false,
            error: false,
          });
        } catch (error) {
          logger.log(error, 'NotificationsStore - loadNotifications - error');
          patchState(store, {
            items: [],
            loading: false,
            error: true,
          });
        }
      },

      /**
       * Limpia el estado de las notificaciones
       */
      clearNotifications(): void {
        patchState(store, initialState);
      },
    }),
  ),
);
